import type { DisplayJob } from '../types';
import { coerceStringArrayField } from './coerce-string-array-field';
import {
  buildRelatedResourceArticleLinks,
  type ResourceArticleLink,
  type ResourceArticleLocale,
} from './build-related-resource-article-links';
import { getJobSearchGuideAbsolutePaths } from './indexation-discovery-links';

export type JobDetailContentGuideLink = {
  title: string;
  url: string;
  priority: 'medium' | 'high';
  category: 'content';
  icon: string;
  description: string;
};

function normalizeLocale(locale: string): ResourceArticleLocale {
  return locale === 'fi' || locale === 'sv' ? locale : 'en';
}

function pickFirstMatchingPath(
  catalog: ResourceArticleLink[],
  needles: string[],
  picked: ResourceArticleLink[],
): void {
  for (const needle of needles) {
    const lowerNeedle = needle.toLowerCase();
    const match = catalog.find((article) => article.path.toLowerCase().includes(lowerNeedle));
    if (match && !picked.some((item) => item.path === match.path)) {
      picked.push(match);
      return;
    }
  }
}

function toGuideLink(article: ResourceArticleLink): JobDetailContentGuideLink {
  return {
    title: article.title,
    url: article.path,
    priority: 'medium',
    category: 'content',
    icon: '📚',
    description: article.description,
  };
}

/**
 * Maps job attributes to specific resource guides (not generic hub URLs).
 * Returns at most four links, ordered by relevance.
 */
export function buildJobDetailContentGuideLinks(
  job: DisplayJob,
  locale: string,
): JobDetailContentGuideLink[] {
  const loc = normalizeLocale(locale);
  const catalog = buildRelatedResourceArticleLinks(loc);
  const picked: ResourceArticleLink[] = [];

  const locationBlob = coerceStringArrayField(job.location).join(' ').toLowerCase();
  if (locationBlob.includes('helsinki') || locationBlob.includes('helsingfors')) {
    pickFirstMatchingPath(
      catalog,
      ['helsinki-job-market', 'helsingin-tyomarkkinat', 'helsingfors-arbetsmarknad'],
      picked,
    );
  }
  if (locationBlob.includes('tampere') || locationBlob.includes('tammerfors')) {
    pickFirstMatchingPath(
      catalog,
      ['tampere-job-market', 'tampereen-tyomarkkinat', 'tammerfors-arbetsmarknad'],
      picked,
    );
  }

  const remoteness = (job.remoteness ?? '').toLowerCase();
  if (remoteness.includes('remote') || remoteness.includes('hybrid')) {
    pickFirstMatchingPath(catalog, ['remote-work-in-finland', 'etatyo-suomessa', 'distansarbete-i-finland'], picked);
  }

  const industry = (job.companyIndustry ?? '').toLowerCase();
  const field = (job.field ?? '').toLowerCase();
  if (
    industry.includes('technology') ||
    industry.includes('software') ||
    field.includes('software') ||
    field.includes('engineering') ||
    field.includes('technology')
  ) {
    pickFirstMatchingPath(
      catalog,
      [
        'technology-salary-guide',
        'teknologia-palkkaopas',
        'teknologi-loeneguide',
        'teknikloner',
        'cool-finnish-tech',
        'tech-yritykset',
        'techbolag',
      ],
      picked,
    );
  }

  if (job.salaryRangeMin != null || job.salaryRangeMax != null) {
    pickFirstMatchingPath(
      catalog,
      [
        'salary-transparency-in-finnish',
        'palkkalapinakyvyys-suomalaisissa',
        'lonetransparens-i-finska',
        'highest-salaries',
        'korkeimmat-palkat',
        'hogsta-lonerna',
      ],
      picked,
    );
  }

  const requiredLanguages = coerceStringArrayField(job.requiredLanguages).join(' ').toLowerCase();
  if (requiredLanguages.includes('english')) {
    pickFirstMatchingPath(
      catalog,
      ['cool-finnish-tech', 'tech-yritykset', 'techbolag', 'best-job-websites', 'parhaat-tyopaikkasivustot', 'basta-jobbsajter'],
      picked,
    );
  }

  const roleLevel = (job.normalisedRoleLevel ?? '').toLowerCase();
  if (roleLevel.includes('junior') || roleLevel.includes('entry') || roleLevel.includes('graduate')) {
    pickFirstMatchingPath(
      catalog,
      ['hidden-job-market', 'piilotyomarkkinat', 'dolda-arbetsmarknad'],
      picked,
    );
  }

  pickFirstMatchingPath(
    catalog,
    ['best-job-websites-finland', 'parhaat-tyopaikkasivustot', 'basta-jobbsajter'],
    picked,
  );

  const pillarPath = getJobSearchGuideAbsolutePaths(loc).pillar;
  const pillarArticle = catalog.find((article) => article.path === pillarPath);
  if (pillarArticle && !picked.some((item) => item.path === pillarArticle.path)) {
    picked.push(pillarArticle);
  }

  return picked.slice(0, 4).map(toGuideLink);
}
